import React from "react";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  HStack,
  Link,
  Text,
} from "@chakra-ui/react";
import DefaultLayout from "../layouts/default";

const WikiLink = () => {
  return (
    <HStack justifySelf={"flex-end"} mt={"10px"} alignItems={"flex-start"}>
      <Text>Read more in our </Text>
      <Link
        href="/docs/why_fql"
        target="_blank"
        color={"brand.500"}
        fontWeight={"700"}
      >
        Wiki
      </Link>
    </HStack>
  );
};

const FAQs = () => {
  const faqs = [
    {
      q: "What are FQL Boards?",
      a: `FQL Boards are a structured way to manage and track work progress using Form Query Language (FQL) within an Application Base. 
			FQL Boards allows users to create and manage boards that contain items and columns, where columns represent different statuses 
			of work progress and moving items between columns represents workflow progression.`,
    },
    {
      q: "Why are they built on FQL?",
      a: `FQL Boards are built on Form Query Language (FQL) because FQL provides a human-readable, structured, and efficient way to model 
			and manage work progress, ensuring data integrity, transparency, and ease of use. FQL ensures consistency by enforcing business rules 
			across Forms, avoiding issues like optimistic locking failures and allowing users to interact with boards in a readable format, reducing 
			SQL complexity.`,
    },
    {
      q: "What is an Application Base?",
      a: `An Application Base is a high-level abstraction layer for building applications that shifts the focus from traditional database tables 
			and procedural logic to forms and user transactions. It acts as a foundation that enables structured, human-readable data management without 
			requiring extensive database or programming knowledge. Instead of working directly with tables and queries, you work with forms that represent 
			user transactions and by focusing on structured logic rather than complex queries, it enables natural language-based interactions, moving towards 
			a more conversational way of building applications.`,
    },
    {
      q: "What is a Form in FQL?",
      a: `A Form in FQL represents a user transaction — a logical unit of business activity, such as registering a customer, creating an order, or updating 
      a booking. Forms are the core abstraction in FQL, replacing tables as the primary building block.`
    },
    {
      q: "How does FQL differ from SQL?",
      a: `Unlike SQL, which focuses on low-level table operations, FQL is designed to be human-readable and operates at the level of user transactions. 
      FQL abstracts complex joins, constraints, and business logic into simple, structured statements that mirror real-world workflows.`
    },
    {
      q: "Can non-programmers use FQL?",
      a: `Yes! FQL is designed to be readable by everyone, including end users. While writing FQL may require some structural thinking, its syntax is 
      intentionally simple and human-centric, making it accessible even to non-developers.`
    },
    {
      q: "How does FQL handle data integrity?",
      a: `FQL enforces business-level data integrity by checking all records involved in a transaction. Unlike traditional DB constraints that work at 
      the table/record level, FQL ensures consistency across the entire user interaction.`
    },
    {
      q: `What makes FQL “intelligently simple”?`,
      a: `FQL uses internal graph traversal (path inference) to interpret relationships between forms. This allows users to write concise queries without 
      explicitly managing joins, while still maintaining unambiguous and accurate results.`
    },
    {
      q: "Can FQL replace traditional programming?",
      a: `FQL isn’t a full programming language but rather a domain-specific language for modeling applications. While it doesn’t replace general-purpose code, 
      it significantly reduces the amount of code needed to model business logic, especially when paired with an Application Base.`
    },
    {
      q: "Is FQL compatible with AI and No-Code platforms?",
      a: `Yes. FQL complements AI and No-Code paradigms by offering a structured, transparent, and explainable layer for application logic. It’s especially 
      useful in AI-assisted development workflows, where readable output is crucial.`
    },
    {
      q: "How does FQL improve maintainability?",
      a: `Because FQL statements reflect business-level logic in a human-readable format, they’re easier to review, modify, and audit. This reduces long-term 
      maintenance costs and minimizes reliance on specialized developers.`
    },
    {
      q: "What types of data can be used in FQL forms?",
      a: `FQL supports a range of data types including number, boolean, text, date, time, file, and image. This allows it to model a wide variety of application 
      domains and user interactions with clarity.`
    }
  ];

  return (
    <DefaultLayout>
      <Text ml={"10%"} fontWeight={"700"} fontSize={"30px"} mb={"20px"}>
        Frequently Asked Questions
      </Text>
      <Box overflow={"auto"} height={"75vh"} mb={"5vh"} w={"100%"}>
        <Accordion allowMultiple width={"50%"} m={"auto"}>
          {faqs.map((faq, i) => {
            return (
              <AccordionItem border={"none"} key={`faq-${i}`} mb={"10px"}>
                <AccordionButton
                  p={2}
                  borderRadius={"md"}
                  justifyContent={"space-between"}
                >
                  <Text fontWeight={"600"} fontSize={"25px"}>
                    {faq["q"]}
                  </Text>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pb={0}>
                  <Text
                    fontWeight={450}
                    textAlign={"justify"}
                    fontSize={"18px"}
                  >
                    {faq["a"]}
                  </Text>
                  <WikiLink />
                </AccordionPanel>
              </AccordionItem>
            );
          })}
        </Accordion>
      </Box>
    </DefaultLayout>
  );
};

export default FAQs;
